import { DEPT_OPTIONS } from '../featureFormConstants';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import { getModuleScope } from '../../../../types/feature';
import type { FeatureDepartment, MultiDeptValue } from '../../../../types/feature';

interface Props {
  department: FeatureDepartment;
  useMultipleDepts: boolean;
  selectedDepartments: MultiDeptValue[];
  moduleInput: string;
  error?: string;
  /**
   * Fired when a single business unit is picked. The CALLER must also reset
   * `useMultipleDepts` to false (this controlled component only reports the choice),
   * e.g. `onSelectSingle={(d) => { setDepartment(d); setUseMultipleDepts(false); }}`.
   */
  onSelectSingle: (d: FeatureDepartment) => void;
  onUseMultiple: () => void;
  onToggleDept: (d: MultiDeptValue) => void;
}

export function BusinessUnitSelector({
  department, useMultipleDepts, selectedDepartments, moduleInput, error,
  onSelectSingle, onUseMultiple, onToggleDept,
}: Props) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {DEPT_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelectSingle(opt.value)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all"
            style={
              !useMultipleDepts && department === opt.value
                ? {
                    borderColor: SOVEREIGN.brand,
                    background: 'rgba(51,146,116,0.09)',
                    color: SOVEREIGN.bottle,
                    boxShadow: `0 2px 8px ${SOVEREIGN.brand}22`,
                  }
                : {
                    borderColor: SOVEREIGN.cardBorder,
                    background: SOVEREIGN.cardBg,
                    color: SOVEREIGN.kpiCaption,
                  }
            }
          >
            <span>{opt.icon}</span>
            <div className="text-left">
              <div className="font-bold">{opt.shortLabel}</div>
              <div className="text-xs font-normal opacity-70 leading-tight">{opt.label.split('—')[1]?.trim() ?? ''}</div>
            </div>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onUseMultiple}
        className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all w-full"
        style={
          useMultipleDepts
            ? { borderColor: SOVEREIGN.brand, background: 'rgba(51,146,116,0.07)', color: SOVEREIGN.bottle }
            : { borderColor: SOVEREIGN.cardBorder, background: SOVEREIGN.cardBg, color: SOVEREIGN.kpiCaption }
        }
      >
        <span>📋</span>
        <span>Common for selected business units only (e.g. OMS + AMS)</span>
      </button>
      {useMultipleDepts && (
        <div
          className="mt-3 p-3 rounded-xl"
          style={{ background: SOVEREIGN.inputBg, border: `1px solid ${SOVEREIGN.cardBorder}` }}
        >
          <p
            className="text-xs font-medium mb-2"
            style={{ color: SOVEREIGN.ink }}
          >
            Select business units that can see this feature:
          </p>
          <div className="flex flex-wrap gap-3">
            {(['OMS', 'AMS', 'EPC'] as const).map(d => (
              <label key={d} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedDepartments.includes(d)}
                  onChange={() => onToggleDept(d)}
                  className="rounded"
                  style={{ accentColor: SOVEREIGN.bottle }}
                />
                <span
                  className="text-sm font-medium"
                  style={{ color: SOVEREIGN.ink }}
                >
                  {d}
                </span>
              </label>
            ))}
          </div>
          <p
            className="text-xs mt-2"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Selected: {selectedDepartments.length ? selectedDepartments.join(', ') : 'none'}
          </p>
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
      )}
      {moduleInput.trim() && (() => {
        const scope = getModuleScope(moduleInput.trim() + '.read');
        const isShared = scope?.dataScope === 'shared';
        const isSingleBU = !useMultipleDepts && department !== 'COMMON';
        const showMisconfig = isShared && isSingleBU;
        return (
          <>
            {isShared && (
              <div
                className="mt-3 p-3 rounded-xl text-xs"
                style={{
                  border: `1px solid ${SOVEREIGN.cardBorder}`,
                  background: 'rgba(31,90,71,0.06)',
                  color: SOVEREIGN.bottle,
                }}
              >
                <span className="font-semibold">Shared data module.</span> Records created here are visible across all business units.
              </div>
            )}
            {showMisconfig && (
              <div
                className="mt-3 p-3 rounded-xl text-xs flex items-center gap-2"
                style={{
                  border: `1px solid ${SOVEREIGN.champagne}`,
                  background: 'rgba(201,169,97,0.08)',
                  color: SOVEREIGN.bottle,
                }}
              >
                <span className="font-semibold">Consider setting to Common.</span> This module uses shared data; assigning to a single business unit may be misleading.
              </div>
            )}
          </>
        );
      })()}
      <p
        className="text-xs mt-2"
        style={{ color: SOVEREIGN.kpiCaption }}
      >
        COMMON = all business units. Single = one business unit. Multiple = visible in 2 or 3 selected business units only.
      </p>
    </div>
  );
}
